import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props { children: ReactNode }
interface State { hasError: boolean; message: string | null }

// Patterns that mean "the browser has the OLD bundle but the server has
// a NEW deploy — chunk hashes don't match". Vite + code-splitting produces
// hashed filenames per build; when the user has been on the page across
// a deploy, navigating to a lazy route fails with one of these messages.
// We auto-reload in this case — the user has nothing to do, and a forced
// reload picks up the new bundle.
function isStaleChunkError(message: string | null): boolean {
  if (!message) return false
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  )
}

// One-shot guard: if a reload itself fails to fix the problem, don't
// reload-loop the user. We mark the attempt in sessionStorage so a
// second occurrence falls through to the manual error card.
const RELOAD_FLAG = 'ss.stale-chunk-reload-attempted'

// Top-level error boundary so a thrown error doesn't blank the screen.
// Sentry init in main.tsx captures the stack; the user just sees a recoverable
// "something went wrong" card with a "reload" button — unless the error is
// a stale-bundle one, in which case we auto-reload silently.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: null }

  static getDerivedStateFromError(err: unknown): State {
    return { hasError: true, message: err instanceof Error ? err.message : 'Unknown error' }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)

    // Stale-bundle? Auto-reload (once). Avoids dropping the user on the
    // 'Sorry — something tripped up' card every time we deploy mid-session.
    if (isStaleChunkError(error.message)) {
      let already = false
      try { already = sessionStorage.getItem(RELOAD_FLAG) === '1' } catch {}
      if (!already) {
        try { sessionStorage.setItem(RELOAD_FLAG, '1') } catch {}
        // Tiny delay so React has a chance to render a placeholder if
        // anything is waiting. Don't await — we're in a sync hook.
        setTimeout(() => window.location.reload(), 50)
        return
      }
    } else {
      // Non-stale error — clear the reload flag so a future stale error
      // gets its one auto-reload attempt fresh.
      try { sessionStorage.removeItem(RELOAD_FLAG) } catch {}
    }

    // Sentry capture, if loaded. Import lazily so a missing SDK never blocks
    // the error boundary itself.
    import('@sentry/react').then(s => {
      try { (s as any).captureException?.(error, { extra: { componentStack: info.componentStack } }) }
      catch {}
    }).catch(() => {})
  }

  reset = () => {
    try { sessionStorage.removeItem(RELOAD_FLAG) } catch {}
    this.setState({ hasError: false, message: null })
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children
    const stale = isStaleChunkError(this.state.message)
    return (
      <>
        <header className="app-bar"><h1>Something went wrong</h1></header>
        <main>
          <div className="card">
            <h3>Sorry — something tripped up</h3>
            <p className="muted">
              {stale
                ? "The app updated since you opened it. Tap Reload to get the new version."
                : "Your work is safe. Reload the page and we'll pick up where you left off."}
            </p>
            {this.state.message && (
              <p className="help" style={{ marginTop: 'var(--space-2)', fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{this.state.message}</p>
            )}
            <button className="btn btn-primary" style={{ marginTop: 'var(--space-3)' }} onClick={this.reset}>Reload</button>
          </div>
        </main>
      </>
    )
  }
}
